export type FontSizes = 'extra-small' | 'medium' | 'small';

/**
 * Maps a font size token to its corresponding `font-size` CSS value.
 * This utility provides consistent font sizing across components by translating
 * semantic font size identifiers into concrete `rem` values. If an unsupported
 * size is provided, it defaults to `'medium'`.
 *
 * @param props       An object containing the `$size` property.
 * @param props.$size The font size token (`'extra-small'`, `'small'`, or `'medium'`).
 * @returns A string representing the CSS `font-size` value in `rem`.
 *
 * @example
 * ```tsx
 * const fontSize = getFontSize('small'); // "1.4rem"
 * ```
 */
export const getFontSize = ({$size}: {$size?: FontSizes}) => {
    switch ($size) {
        case 'extra-small':
            return '1.2rem';
        case 'small':
            return '1.4rem';
        case 'medium':
        case undefined:
        default:
            return '1.6rem';
    }
};

/**
 * Maps a font size token to its corresponding `line-height` CSS value.
 * This utility ensures visual balance and readability by associating
 * semantic font size identifiers with specific line-height values.
 * If an unsupported size is provided, it defaults to `'medium'`.
 *
 * @param props       An object containing the `$size` property.
 * @param props.$size The font size token (`'extra-small'`, `'small'`, or `'medium'`).
 * @returns A string representing the CSS `line-height` value.
 *
 * @example
 * ```tsx
 * const lineHeight = getLineHeight('extra-small'); // "1.5"
 * ```
 */
export const getLineHeight = ({$size}: {$size?: FontSizes}) => {
    switch ($size) {
        case 'extra-small':
            return '1.5';
        case 'small':
            return '1.4';
        case 'medium':
        case undefined:
        default:
            return '1.3';
    }
};